import { useRef, type KeyboardEvent, type ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface RadioButtonOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

/** Arrow keys move (wrapping), Home/End jump; null for any other key. */
function nextIndex(
  key: string,
  current: number,
  length: number
): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown')
    return (current + 1) % length;
  if (key === 'ArrowLeft' || key === 'ArrowUp') {
    return (current - 1 + length) % length;
  }
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return null;
}

/**
 * A row of outline buttons as a WAI-ARIA radiogroup: roving tabindex, arrows
 * move and select, Home/End jump. The one option-row control on Settings →
 * Preferences. While `disabled` the options stay focusable (aria-disabled) but
 * ignore clicks and keys.
 */
export function RadioButtonGroup<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: {
  options: readonly RadioButtonOption<T>[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}): ReactElement {
  const optionRefs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  const select = (next: T): void => {
    if (disabled) return;
    if (next !== value) onChange(next);
    optionRefs.current[next]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const current = options.findIndex(o => o.value === value);
    if (current < 0) return;
    const next = nextIndex(event.key, current, options.length);
    if (next === null) return;
    event.preventDefault();
    select(options[next].value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cn(
        'flex flex-wrap gap-3',
        disabled && 'opacity-60 pointer-events-none'
      )}
    >
      {options.map(option => {
        const Icon = option.icon;
        const isActive = option.value === value;
        return (
          <Button
            key={option.value}
            ref={el => {
              optionRefs.current[option.value] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-disabled={disabled}
            tabIndex={isActive ? 0 : -1}
            variant="outline"
            size="sm"
            onClick={() => select(option.value)}
            onKeyDown={handleKeyDown}
            className={cn(
              'gap-2',
              isActive &&
                'bg-accent text-accent-foreground border-accent-border hover:bg-accent'
            )}
          >
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
